import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { logError } from "../lib/logger";
import { FONT_HEAD, FONT_BODY, NAVY, GOLD } from "../lib/constants";

const INPUT_STYLE = {
  fontFamily: FONT_BODY,
  border: "1px solid rgba(27,58,107,0.22)",
  borderRadius: "2px",
  fontSize: "14px",
  color: "#2D2D2D",
  background: "#FFFFFF",
};

const slugify = (s) =>
  s.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").replace(/-+/g, "-");

export const sectionsToText = (sections = []) =>
  sections
    .map((s) => {
      const parts = [`## ${s.h}`];
      if (s.p?.length) parts.push(s.p.join("\n\n"));
      if (s.list?.length) parts.push(s.list.map((i) => `- ${i}`).join("\n"));
      return parts.join("\n\n");
    })
    .join("\n\n");

const makeSectionParser = () => {
  const sections = [];
  let current = null;
  let para = [];
  const flushPara = () => {
    if (!current || !para.length) return;
    current.p = [...(current.p || []), para.join(" ")];
    para = [];
  };
  const startSection = (line) => {
    flushPara();
    current = { h: line.slice(3).trim() };
    sections.push(current);
  };
  const addListItem = (line) => {
    flushPara();
    if (current) current.list = [...(current.list || []), line.slice(2).trim()];
  };
  const feed = (raw) => {
    const line = raw.trimEnd();
    if (line.startsWith("## ")) return startSection(line);
    if (line.startsWith("- ")) return addListItem(line);
    if (line.trim() === "") return flushPara();
    if (current) para.push(line.trim());
  };
  const finish = () => {
    flushPara();
    return sections;
  };
  return { feed, finish };
};

export const textToSections = (text) => {
  const parser = makeSectionParser();
  text.split("\n").forEach(parser.feed);
  return parser.finish();
};

const EMPTY = {
  slug: "", title: "", excerpt: "", date: "July 2026", readTime: "5 min read",
  image: "", content: "", published: true,
};

const FormField = ({ label, children }) => (
  <div>
    <label className="block text-xs font-bold mb-1.5 uppercase tracking-wide" style={{ color: NAVY, fontFamily: FONT_BODY }}>
      {label}
    </label>
    {children}
  </div>
);

const BlogFormFields = ({ f, set, onTitle }) => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FormField label="Title *">
        <input value={f.title} onChange={onTitle} data-testid="blog-form-title" className="w-full px-3 py-2.5" style={INPUT_STYLE} />
      </FormField>
      <FormField label="Slug (URL) *">
        <input value={f.slug} onChange={set("slug")} data-testid="blog-form-slug" className="w-full px-3 py-2.5" style={INPUT_STYLE} />
      </FormField>
    </div>
    <FormField label="Excerpt (shown in blog list & Google) *">
      <textarea value={f.excerpt} onChange={set("excerpt")} data-testid="blog-form-excerpt" rows={2} className="w-full px-3 py-2.5 resize-none" style={INPUT_STYLE} />
    </FormField>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <FormField label="Image URL *">
        <input value={f.image} onChange={set("image")} data-testid="blog-form-image" className="w-full px-3 py-2.5" style={INPUT_STYLE} />
      </FormField>
      <FormField label="Date label">
        <input value={f.date} onChange={set("date")} data-testid="blog-form-date" className="w-full px-3 py-2.5" style={INPUT_STYLE} />
      </FormField>
      <FormField label="Read time">
        <input value={f.readTime} onChange={set("readTime")} data-testid="blog-form-readtime" className="w-full px-3 py-2.5" style={INPUT_STYLE} />
      </FormField>
    </div>
    <FormField label="Content — use '## ' for section headings, '- ' for bullet items, blank line between paragraphs *">
      <textarea value={f.content} onChange={set("content")} data-testid="blog-form-content" rows={14} className="w-full px-3 py-2.5 font-mono text-sm" style={INPUT_STYLE} />
    </FormField>
  </>
);

const BlogFormActions = ({ f, onSave, onCancel, saving }) => (
  <div className="flex gap-3">
    <button
      onClick={() => onSave(f)}
      disabled={saving}
      data-testid="blog-form-save"
      className="px-5 py-2.5 text-sm font-bold transition-all hover:opacity-90 disabled:opacity-50"
      style={{ background: NAVY, color: "#FFFFFF", fontFamily: FONT_BODY }}
    >
      {saving ? "Saving..." : "Save Post"}
    </button>
    <button
      onClick={onCancel}
      data-testid="blog-form-cancel"
      className="px-5 py-2.5 text-sm font-bold border-2 transition-all hover:bg-gray-50"
      style={{ borderColor: NAVY, color: NAVY, fontFamily: FONT_BODY }}
    >
      Cancel
    </button>
  </div>
);

const BlogForm = ({ initial, onSave, onCancel, saving, error }) => {
  const [f, setF] = useState(initial);
  const isNew = !initial.slug;
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  const onTitle = (e) => {
    const title = e.target.value;
    setF((prev) => ({ ...prev, title, slug: isNew ? slugify(title) : prev.slug }));
  };

  return (
    <div className="bg-white p-6 space-y-4" style={{ border: "1px solid rgba(27,58,107,0.15)" }} data-testid="blog-form">
      <h3 className="text-lg font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
        {isNew ? "New Post" : `Edit: ${initial.title}`}
      </h3>
      <BlogFormFields f={f} set={set} onTitle={onTitle} />
      <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold" style={{ fontFamily: FONT_BODY, color: "#2D2D2D" }}>
        <input
          type="checkbox"
          checked={f.published}
          onChange={(e) => setF((prev) => ({ ...prev, published: e.target.checked }))}
          data-testid="blog-form-published"
        />
        Published (visible on the website)
      </label>
      {error && (
        <div data-testid="blog-form-error" className="px-3 py-2 text-xs" style={{ background: "rgba(239,68,68,0.07)", color: "#DC2626", fontFamily: FONT_BODY }}>
          {error}
        </div>
      )}
      <BlogFormActions f={f} onSave={onSave} onCancel={onCancel} saving={saving} />
    </div>
  );
};

const PostRow = ({ post, onEdit, onDelete }) => (
  <div
    data-testid={`blog-post-row-${post.slug}`}
    className="bg-white p-4 flex items-center gap-4"
    style={{ border: "1px solid rgba(27,58,107,0.12)" }}
  >
    <img src={post.image} alt="" className="w-16 h-12 object-cover flex-shrink-0 hidden sm:block" />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-bold truncate" style={{ fontFamily: FONT_HEAD, color: NAVY }}>{post.title}</p>
      <p className="text-xs truncate" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>/blog/{post.slug} · {post.date}</p>
    </div>
    <span
      className="flex items-center gap-1 text-[11px] font-bold px-2 py-1 flex-shrink-0"
      style={{ background: post.published ? "rgba(212,168,67,0.18)" : "#F3F4F6", color: post.published ? "#b8922a" : "#6B7280", fontFamily: FONT_BODY }}
    >
      {post.published ? <Eye size={11} /> : <EyeOff size={11} />}
      {post.published ? "Published" : "Draft"}
    </span>
    <button onClick={() => onEdit(post)} data-testid={`blog-edit-${post.slug}`} title="Edit"
      className="p-2 transition-colors hover:bg-gray-100" style={{ color: NAVY }}>
      <Pencil size={15} />
    </button>
    <button onClick={() => onDelete(post)} data-testid={`blog-delete-${post.slug}`} title="Delete"
      className="p-2 transition-colors hover:bg-red-50" style={{ color: "#DC2626" }}>
      <Trash2 size={15} />
    </button>
  </div>
);

const buildPostPayload = (f) => {
  const sections = textToSections(f.content);
  if (!f.title || !f.slug || !f.excerpt || !f.image || sections.length === 0) return null;
  return {
    slug: f.slug, title: f.title, excerpt: f.excerpt, date: f.date,
    readTime: f.readTime, image: f.image, sections, published: f.published,
  };
};

const savePost = async (api, editing, payload) => {
  try {
    if (editing.slug) await api.put(`/admin/blog/${editing.slug}`, payload);
    else await api.post("/admin/blog", payload);
    return null;
  } catch (err) {
    return err.response?.data?.detail || "Save failed. Check the slug is unique.";
  }
};

const fetchPosts = (api) =>
  api
    .get("/admin/blog")
    .then((res) => res.data.items)
    .catch((err) => {
      logError("Blog load failed", err);
      return [];
    });

const usePostManager = (api) => {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => fetchPosts(api).then(setPosts), [api, setPosts]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (f) => {
    const payload = buildPostPayload(f);
    if (!payload) {
      setError("Title, slug, excerpt, image and at least one '## Section' are required.");
      return;
    }
    setSaving(true);
    setError("");
    const errMsg = await savePost(api, editing, payload);
    setSaving(false);
    if (errMsg) {
      setError(errMsg);
      return;
    }
    setEditing(null);
    await load();
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blog/${post.slug}`);
      await load();
    } catch (err) {
      logError("Blog delete failed", err);
    }
  };

  return { posts, editing, setEditing, saving, error, setError, save, remove };
};

export const BlogManager = ({ api }) => {
  const { posts, editing, setEditing, saving, error, setError, save, remove } = usePostManager(api);

  if (editing)
    return (
      <BlogForm
        initial={editing.slug ? { ...editing, content: sectionsToText(editing.sections) } : EMPTY}
        onSave={save}
        onCancel={() => { setEditing(null); setError(""); }}
        saving={saving}
        error={error}
      />
    );

  return (
    <div className="space-y-3">
      <button
        onClick={() => setEditing({})}
        data-testid="blog-new-post-btn"
        className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold transition-all hover:opacity-90"
        style={{ background: GOLD, color: NAVY, fontFamily: FONT_BODY }}
      >
        <Plus size={15} />
        New Post
      </button>
      {posts === null && <p className="text-sm" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>Loading…</p>}
      {posts?.length === 0 && <p className="text-sm" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>No posts yet.</p>}
      {posts?.map((p) => (
        <PostRow key={p.slug} post={p} onEdit={setEditing} onDelete={remove} />
      ))}
    </div>
  );
};
